import { useMemo } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useSessionStore } from '../../stores/sessionStore'
import { JournalEntryType } from '@api/types/session'

export default function StatsBar() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const journal = useSessionStore((s) => s.journal)
  const cluesUsed = useSessionStore((s) => s.cluesUsed)

  const { solved, missed } = useMemo(() => {
    let solved = 0
    let missed = 0

    for (const entry of journal) {
      if (
        entry.entryType === JournalEntryType.ChoiceMade ||
        entry.entryType === JournalEntryType.AnswerSubmitted
      ) {
        const content = typeof entry.content === 'string'
          ? JSON.parse(entry.content)
          : entry.content || {}

        const branchKey = content.BranchKey || content.branchKey || ''
        const isNeutral = branchKey === 'skip' || branchKey === 'cancel' || branchKey === 'timeout'
        if (isNeutral) continue

        // Filter out condition evaluations (Decision nodes)
        if (content.ConditionsEvaluated !== undefined) continue

        const isCorrect = content.IsCorrect ?? content.isCorrect ?? (branchKey === 'correct')
        if (isCorrect) {
          solved++
        } else {
          missed++
        }
      }
    }

    return { solved, missed }
  }, [journal])

  const stats = [
    { value: solved, label: 'Solved', color: 'success.main' },
    { value: missed, label: 'Missed', color: 'error.main' },
    { value: cluesUsed, label: 'Hints Used', color: 'warning.main' },
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#16213e' : 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        px: isMobile ? 1 : 2,
        py: isMobile ? 0.5 : 1,
        flexShrink: 0,
      }}
    >
      {stats.map((stat, i) => (
        <Box key={stat.label} sx={{ display: 'contents' }}>
          {i > 0 && (
            <Box
              sx={{
                width: '1px',
                alignSelf: 'stretch',
                bgcolor: 'divider',
              }}
            />
          )}
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: 'center', gap: isMobile ? '4px' : '2px' }}>
            <Typography
              sx={{
                fontSize: isMobile ? '0.8125rem' : '1rem',
                fontWeight: 700,
                color: stat.color,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              sx={{
                fontSize: isMobile ? '0.5625rem' : '0.625rem',
                color: 'text.disabled',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 500,
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  )
}
